// Saved settings that hold records of the user's own organisation (team ids,
// cargo tag ids) are kept per user. localStorage is shared by every account
// used in the browser, so a second account (another org, or a colleague on the
// same PC) otherwise starts out with filters pointing at teams it doesn't have.
//
// Device preferences (dark mode, language, panel sizes, sort orders) are not
// org data and stay in plain localStorage.

let storageUserId: string | null = null;

// Called once the logged-in user is known, and with null on logout. Until then
// reads come back empty and writes are dropped.
export const setStorageUser = (userId: string | null) => {
  storageUserId = userId;
};

const scopedKey = (key: string, userId: string) => `${key}:${userId}`;

export const userStorage = {
  getItem(key: string): string | null {
    if (!storageUserId) return null;
    const scoped = localStorage.getItem(scopedKey(key, storageUserId));
    if (scoped !== null) return scoped;
    // Settings saved before they were kept per user: the first user to load
    // them on this browser adopts them, almost always the one who saved them.
    const legacy = localStorage.getItem(key);
    if (legacy === null) return null;
    localStorage.setItem(scopedKey(key, storageUserId), legacy);
    localStorage.removeItem(key);
    return legacy;
  },
  setItem(key: string, value: string) {
    if (!storageUserId) return;
    localStorage.setItem(scopedKey(key, storageUserId), value);
  },
};

const parse = (raw: string | null): unknown => {
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// Keeps only the saved ids that are still among the user's teams. A filter
// that pointed only at teams the user no longer has (deleted, left, or another
// account's) comes back as null, so the caller falls back to all teams; a
// filter the user deliberately emptied stays empty.
const keepCurrentTeams = (
  saved: unknown,
  teamIds: string[]
): string[] | null => {
  if (!Array.isArray(saved)) return null;
  const kept = saved.filter(
    (id): id is string => typeof id === "string" && teamIds.includes(id)
  );
  return saved.length > 0 && kept.length === 0 ? null : kept;
};

// A saved team-id list, or null when there is none to use.
export const readSavedTeamIds = (
  key: string,
  teamIds: string[]
): string[] | null => keepCurrentTeams(parse(userStorage.getItem(key)), teamIds);

// A saved team-id list per filter type (e.g. { cargos, routes }), or null when
// nothing is saved. A type without a usable saved list gets all teams.
export const readSavedTeamIdsByType = <T extends string>(
  key: string,
  teamIds: string[],
  types: readonly T[]
): Record<T, string[]> | null => {
  const saved = parse(userStorage.getItem(key));
  if (typeof saved !== "object" || saved === null || Array.isArray(saved)) {
    return null;
  }
  return Object.fromEntries(
    types.map((type) => [
      type,
      keepCurrentTeams((saved as Record<string, unknown>)[type], teamIds) ??
        teamIds,
    ])
  ) as Record<T, string[]>;
};

// A saved list of ids that can't be checked yet (e.g. cargo tags, which load
// later), or null when there is none.
export const readSavedIds = (key: string): string[] | null => {
  const saved = parse(userStorage.getItem(key));
  return Array.isArray(saved)
    ? saved.filter((id): id is string => typeof id === "string")
    : null;
};
